import { useCallback, useEffect, useState } from "react";
import { CloseOutlined } from "@ant-design/icons";
import { Select, Spin, Tabs, message as antMessage } from "antd";
import { serverFetch } from "@server";
import DialogMain from "./dialog-main";
import Messages from "./messages";
import { isMobile } from "../../extensions/design";
import "../clients/clients.css";
import "./dialog-detail.css";

const unwrapDialogPayload = (data) => {
  if (data?.dialog != null) {
    const tags = Array.isArray(data.tags) ? data.tags : [];
    return { dialog: { ...data.dialog, tags }, tags };
  }
  const tags = Array.isArray(data?.tags) ? data.tags : [];
  return { dialog: data, tags };
};

/** Панель диалога по образцу карточки клиента: client-modal-title + custom-tabs-container (clients.css) */
const DialogModal = ({
  id,
  onClose,
  messages,
  setMessages,
  messagesLoading,
  messagesEndRef,
  scrollToBottom,
  onSent,
}) => {
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);
  const [activeTab, setActiveTab] = useState("messages");

  const reloadPayload = useCallback(() => {
    if (!id) return;
    serverFetch(`Message/Dialog/${id}`)
      .then((raw) => {
        const next = unwrapDialogPayload(raw);
        setPayload(next);
      })
      .catch((e) => antMessage.error(e.userMessage));
  }, [id]);

  const handleDialogRead = useCallback((readUpToMessageId, syncLastMessageId = false) => {
    setPayload((prev) => {
      if (!prev?.dialog) return prev;

      let nextReadId;
      if (readUpToMessageId != null) {
        const currentRead = prev.dialog.lastMessageIdIsRead;
        if (currentRead != null && Number(currentRead) >= Number(readUpToMessageId)) {
          return prev;
        }
        nextReadId = readUpToMessageId;
      } else {
        nextReadId = prev.dialog.lastMessageId;
      }

      return {
        ...prev,
        dialog: {
          ...prev.dialog,
          lastMessageIdIsRead: nextReadId,
          ...(syncLastMessageId && readUpToMessageId != null
            ? { lastMessageId: readUpToMessageId }
            : {}),
        },
      };
    });
  }, []);

  const handleDialogUnread = useCallback((readUpToMessageId) => {
    setPayload((prev) => {
      if (!prev?.dialog) return prev;
      return {
        ...prev,
        dialog: {
          ...prev.dialog,
          lastMessageIdIsRead: readUpToMessageId ?? null,
        },
      };
    });
  }, []);

  const handleTabChange = useCallback((key) => {
    setActiveTab(key);
    if (key === "messages") {
      setTimeout(scrollToBottom, 0);
    }
  }, [scrollToBottom]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setPayload(null);
    setActiveTab("messages");
    serverFetch(`Message/Dialog/${id}`)
      .then((raw) => setPayload(unwrapDialogPayload(raw)))
      .catch((e) => {
        antMessage.error(e.userMessage);
        onClose(false);
      })
      .finally(() => setLoading(false));
  }, [id, onClose]);

  const handleCancel = () => onClose(false);

  if (!id) return null;

  const d = payload?.dialog;
  const headerTitle =
    (d?.clientName != null && String(d.clientName).trim()) ||
    (d?.enteredName != null && String(d.enteredName).trim()) ||
    (d?.name != null && String(d.name).trim()) ||
    (!d ? "Диалог" : `Обращение #${d.id ?? id}`);

  const messagesContent = payload?.dialog ? (
    <Messages dialog={payload.dialog} messages={messages} messagesLoading={messagesLoading} messagesEndRef={messagesEndRef}
      scrollToBottom={scrollToBottom} setMessages={setMessages} onSent={onSent} onDialogRead={handleDialogRead} onDialogUnread={handleDialogUnread}/>
  ) : null;

  const dialogSettingsContent = payload?.dialog ? (
    <DialogMain dialog={payload.dialog} tags={payload.dialog?.tags ?? payload.tags} onReload={reloadPayload} onClose={handleCancel}/>
  ) : null;

  if (isMobile) {
    return (
      <div className="sport-modal">
        <div className="sport-modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && handleCancel()}>
          <div className="sport-modal-content dialog-modal-content dialog-modal-mobile-content" onMouseDown={(e) => e.stopPropagation()}>
            <div className="dialog-mobile-header">
              <div className="dialog-mobile-title">
                <Select className="dialog-mobile-tabs-select" value={activeTab} onChange={handleTabChange}
                  options={[{ value: "messages", label: "Сообщения" }, { value: "dialog", label: "Диалог" }]}/>
                <CloseOutlined className="dialog-mobile-close" onClick={handleCancel} />
              </div>
              <div className="dialog-mobile-client-name">{headerTitle}</div>
            </div>
            <div className="dialog-mobile-body">
              {!payload?.dialog ? (
                <Spin spinning={loading}>
                  <div className="dialog-modal-loading-placeholder" />
                </Spin>
              ) : activeTab === "messages" ? (messagesContent) : (dialogSettingsContent)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sport-modal">
      <div
        className="sport-modal-overlay"
        onMouseDown={(e) => e.target === e.currentTarget && handleCancel()}
      >
        <div
          className="sport-modal-content dialog-modal-content"
          style={{ width: 900 }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="client-modal-title dialog-modal-title">
            <span>{headerTitle}</span>
            <CloseOutlined
              className="sport-section-title-close"
              onClick={handleCancel}
              style={{ zIndex: 9, marginLeft: "auto", cursor: "pointer" }}
            />
          </div>
          <div className="custom-tabs-container dialog-modal-body">
            {!payload?.dialog ? (
              <Spin spinning={loading}>
                <div className="dialog-modal-loading-placeholder" />
              </Spin>
            ) : (
              <Tabs
                type="card"
                className="custom-tabs dialog-modal-tabs"
                activeKey={activeTab}
                onChange={handleTabChange}
                destroyInactiveTabPane={false}
                items={[
                  {
                    key: "messages",
                    label: "Сообщения",
                    children: (
                      <Messages
                        dialog={payload.dialog}
                        messages={messages}
                        messagesLoading={messagesLoading}
                        messagesEndRef={messagesEndRef}
                        scrollToBottom={scrollToBottom}
                        setMessages={setMessages}
                        onSent={onSent}
                        onDialogRead={handleDialogRead}
                        onDialogUnread={handleDialogUnread}
                      />
                    ),
                  },
                  {
                    key: "dialog",
                    label: "Диалог",
                    children: (
                      <DialogMain
                        dialog={payload.dialog}
                        tags={payload.dialog?.tags ?? payload.tags}
                        onReload={reloadPayload}
                        onClose={handleCancel}
                      />
                    ),
                  },
                ]}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DialogModal;
